/**
 * Feb 2026 R293zg · PushToTasksDialog — pick SO lines to push into a
 * chosen project as tasks. Minimal shape: one project for the batch,
 * optional per-line due date + assignee. Users can refine tasks on
 * the Tasks surface afterward.
 */

import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";
import { ListChecks, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";

export default function PushToTasksDialog({ open, onClose, ws, so, onDone }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [fulfil, setFulfil] = useState(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [picks, setPicks] = useState({}); // {line_id: {due_date}}

  useEffect(() => {
    if (!open || !ws || !so?.id) return;
    let cancelled = false;
    setLoading(true);
    setPicks({});
    (async () => {
      try {
        const [pr, ff] = await Promise.all([
          api.get(`/workspaces/${ws}/projects?status=active&limit=200`),
          api.get(`/gst/workspaces/${ws}/sales-orders/${so.id}/task-fulfillment`),
        ]);
        if (cancelled) return;
        const rawP = Array.isArray(pr.data) ? pr.data : (pr.data?.items || []);
        setProjects(rawP);
        setFulfil(ff.data);
      } catch (e) {
        toast.error(apiErrorMessage(e, "Could not load projects or SO lines"));
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, ws, so?.id]);

  const lines = fulfil?.lines || [];

  const picksArr = useMemo(() => Object.entries(picks)
    .filter(([, v]) => v?.checked)
    .map(([lineId, v]) => ({
      sales_order_line_id: lineId,
      due_date: v?.due_date || null,
    })), [picks]);

  const togglePick = (lineId) => setPicks((cur) => ({
    ...cur,
    [lineId]: { ...(cur[lineId] || {}), checked: !cur[lineId]?.checked },
  }));
  const setDue = (lineId, v) => setPicks((cur) => ({
    ...cur,
    [lineId]: { ...(cur[lineId] || {}), due_date: v },
  }));
  const toggleAll = () => {
    const allSelected = lines.every((l) => picks[l.id]?.checked);
    const next = {};
    for (const l of lines) {
      next[l.id] = { ...(picks[l.id] || {}), checked: !allSelected };
    }
    setPicks(next);
  };

  const submit = async () => {
    if (!projectId) { toast.error("Pick a project"); return; }
    if (!picksArr.length) { toast.error("Pick at least one line"); return; }
    setPosting(true);
    try {
      const r = await api.post(
        `/gst/workspaces/${ws}/sales-orders/${so.id}/tasks`,
        { project_id: projectId, picks: picksArr });
      const created = r.data.created?.length || 0;
      const skipped = r.data.skipped?.length || 0;
      toast.success(`${created} task${created === 1 ? "" : "s"} created in ${r.data.project?.name}` + (skipped ? ` · ${skipped} skipped (already linked)` : ""));
      onDone?.(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not push tasks"));
    } finally { setPosting(false); }
  };

  if (!so) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="w-screen max-w-full h-[100dvh] p-0 flex flex-col sm:max-w-2xl sm:h-auto sm:max-h-[90vh]"
        data-testid="push-tasks-dialog"
      >
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 border-b border-border">
          <DialogTitle className="flex items-center gap-2 text-base">
            <ListChecks className="h-4 w-4" /> Push {so.doc_no} lines to tasks
          </DialogTitle>
          <DialogDescription className="text-xs">
            Each picked line becomes a task in the chosen project. Delivery status rolls back up to this SO.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-3 space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && (
            <>
              <div>
                <Label className="text-xs">Project</Label>
                <SearchableSelect
                  value={projectId}
                  onChange={setProjectId}
                  options={projects.map((p) => ({ value: p.id, label: p.name }))}
                  placeholder="Pick a project"
                  searchPlaceholder="Search projects…"
                  testid="push-tasks-project"
                />
              </div>

              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                  SO lines
                </div>
                <button type="button" onClick={toggleAll}
                        className="text-[11px] uppercase tracking-widest px-2 py-1 border border-border rounded hover:bg-muted"
                        data-testid="push-tasks-toggle-all">
                  Toggle all
                </button>
              </div>

              <div className="space-y-2" data-testid="push-tasks-lines">
                {lines.map((l) => {
                  const p = picks[l.id] || {};
                  const alreadyLinked = l.task_count > 0;
                  return (
                    <div key={l.id}
                         className={`border rounded p-2 ${p.checked ? "border-primary bg-primary/5" : "border-border bg-background"}`}
                         data-testid={`push-task-line-${l.id}`}>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="checkbox"
                                 className="h-4 w-4 mt-0.5 cursor-pointer"
                                 checked={!!p.checked}
                                 onChange={() => togglePick(l.id)}
                                 data-testid={`push-task-check-${l.id}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                            <span className="truncate">{l.description}</span>
                            {alreadyLinked && (
                              <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 rounded border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200">
                                <CheckCircle2 className="h-2.5 w-2.5" />
                                {l.task_done}/{l.task_count} tasks
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            qty {l.qty}
                          </div>
                          {p.checked && (
                            <div className="mt-2">
                              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Due date (optional)</Label>
                              <Input type="date"
                                      value={p.due_date || ""}
                                      onChange={(e) => setDue(l.id, e.target.value)}
                                      className="h-9 rounded-none text-xs"
                                      data-testid={`push-task-due-${l.id}`} />
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="px-4 sm:px-6 py-3 border-t border-border flex flex-row justify-between gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="push-tasks-cancel">Cancel</Button>
          <Button onClick={submit}
                   disabled={posting || !projectId || !picksArr.length}
                   data-testid="push-tasks-submit">
            {posting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Pushing…</>
                     : <>Push {picksArr.length || ""} task{picksArr.length === 1 ? "" : "s"} <ArrowRight className="h-4 w-4 ml-1" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
